import React, { useState } from 'react';
import {
  Card, CardContent, Typography, IconButton, Chip, Stack, Box, Tooltip, Divider,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import authInstance from '../../services/httpClient';
import { Print as PrintIcon, Download as DownloadIcon } from '@mui/icons-material';
import { printDocument, downloadDocument, runDocumentAction } from '../../utils/orderDocuments';

interface UberOrderItem {
  sku: string;
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface UberOrderData {
  id: string;
  uberOrderId: string;
  displayId: string;
  status: string;
  customerName: string;
  customerPhone: string;
  items: UberOrderItem[];
  totalAmount: number;
  deliveryFee: number;
  placedAt: string;
  legacySaleId?: string;
  vendSaleId?: string | null;
  vendVoidedAt?: string | null;
  saleKeptAt?: string | null;
  scheduled?: boolean;
  scheduledFor?: string | null;
  legacyOrderNumber?: string;
  createdAt: string;
}

interface Props {
  order: UberOrderData;
  isNew?: boolean;
  /** called with the updated fields after a void */
  onChange?: (patch: Partial<UberOrderData>) => void;
}

/** new → collected → delivered, or cancelled at any stage. 'processed' is the older name for collected. */
export const UBER_STATUS: Record<string, { label: string; color: 'warning' | 'info' | 'success' | 'error' }> = {
  new: { label: 'New', color: 'warning' },
  collected: { label: 'Collected', color: 'info' },
  processed: { label: 'Collected', color: 'info' },
  delivered: { label: 'Delivered', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'error' },
};

export default function UberOrderCard({ order, isNew, onChange }: Props) {
  const [voidOpen, setVoidOpen] = useState<false | 'void' | 'keep'>(false);
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  // Never automatic (Jason, 27 Oct 2026): a cancelled / failed order isn't always brought back
  const saleLive = order.status === 'cancelled' && !!order.vendSaleId && !order.vendVoidedAt && !order.saleKeptAt;
  const decide = async (action: 'void' | 'keep') => {
    setVoiding(true);
    setVoidError(null);
    try {
      const { data } = await authInstance.post(`/admin/uber-orders/${order.id}/${action === 'void' ? 'void-sale' : 'keep-sale'}`, {});
      onChange?.(action === 'void' || data?.alreadyVoided
        ? { vendVoidedAt: data?.vendVoidedAt ?? new Date().toISOString() }
        : { saleKeptAt: data?.saleKeptAt ?? new Date().toISOString() });
      setVoidOpen(false);
    } catch (err: any) {
      const d = err?.response?.data;
      setVoidError(d?.userMessage || d?.message || (action === 'void' ? 'The void failed. The sale is still live in Vend.' : 'Could not save that. Try again.'));
    } finally {
      setVoiding(false);
    }
  };
  const st = UBER_STATUS[order.status] ?? { label: order.status || 'New', color: 'warning' as const };
  const collectedOrLater = ['collected', 'processed', 'delivered'].includes(order.status);
  const scheduledText = order.scheduledFor
    ? new Date(order.scheduledFor).toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg' })
    : null;
  const itemCount = order.items?.length || 0;
  const totalQty = order.items?.reduce((s, i) => s + i.quantity, 0) || 0;
  const placedTime = new Date(order.placedAt || order.createdAt).toLocaleTimeString('en-ZA', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg',
  });
  const placedDate = new Date(order.placedAt || order.createdAt).toLocaleDateString('en-ZA', { timeZone: 'Africa/Johannesburg' });

  return (
    <Card
      sx={{
        border: isNew ? '2px solid' : '1px solid',
        borderColor: isNew ? 'success.main' : 'divider',
        transition: 'border-color 0.5s',
      }}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" fontWeight={700} fontFamily="monospace">
            {order.displayId}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {order.scheduled && !collectedOrLater && order.status !== 'cancelled' && (
              <Tooltip title={scheduledText ? `Scheduled for ${scheduledText}` : 'Scheduled order'}>
                <Chip label={scheduledText ? `Scheduled ${scheduledText}` : 'Scheduled'} size="small" color="secondary" variant="outlined" />
              </Tooltip>
            )}
            {collectedOrLater && !order.vendSaleId && !order.legacySaleId && (
              <Tooltip title="The sale has not posted to Vend. It retries when the order is delivered; if it stays, check the backend logs for this order">
                <Chip label="Sale pending" size="small" color="warning" variant="outlined" />
              </Tooltip>
            )}
            <Chip label={st.label} size="small" color={st.color} variant={order.status === 'cancelled' ? 'filled' : 'outlined'} />
            {order.vendVoidedAt && <Chip label="Sale voided" size="small" variant="outlined" />}
            {!order.vendVoidedAt && order.saleKeptAt && <Chip label="Sale kept" size="small" variant="outlined" />}
            <Tooltip title="Print label">
              <IconButton
                size="small"
                color="primary"
                onClick={() => runDocumentAction(printDocument('uberLabel', order.id))}
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download the label as a PDF">
              <IconButton
                size="small"
                onClick={() => runDocumentAction(
                  downloadDocument('uberLabel', order.id, `UE-${order.displayId} Label.pdf`),
                )}
                sx={{ color: 'text.secondary' }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {order.customerName || 'Unknown Customer'}
        </Typography>

        <Divider sx={{ my: 1 }} />

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {itemCount} items ({totalQty} qty)
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            R {Number(order.totalAmount || 0).toFixed(2)}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.5}>
          <Typography variant="caption" color="text.secondary">
            {placedDate} {placedTime}
          </Typography>
          {order.legacyOrderNumber && (
            <Chip label={order.legacyOrderNumber} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
          )}
        </Stack>

        {saleLive && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" mt={1}>
            <Tooltip title="The order was cancelled after it was rung up. If the goods came back to the store, void the sale; if not, leave it.">
              <Chip label="Sale still live in Vend" size="small" color="error" variant="outlined" />
            </Tooltip>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={() => { setVoidError(null); setVoidOpen('keep'); }}>
                Keep sale
              </Button>
              <Button size="small" color="error" variant="outlined" onClick={() => { setVoidError(null); setVoidOpen('void'); }}>
                Void sale?
              </Button>
            </Stack>
          </Stack>
        )}
        <Dialog open={!!voidOpen} onClose={() => !voiding && setVoidOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>{voidOpen === 'keep' ? 'Keep this sale?' : 'Void this sale?'}</DialogTitle>
          <DialogContent>
            {voidOpen === 'keep' ? (
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                The goods for Uber order <strong>{order.displayId}</strong> did not come back, so the sale stays in Vend as it
                is. The card clears from the board.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  This voids Uber order <strong>{order.displayId}</strong> in Vend. The sale is reversed and the stock goes back
                  into Diep River. Only do this if the goods came back to the store.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }} color="error">
                  This cannot be undone. Reinstating the sale means ringing up a new one.
                </Typography>
              </>
            )}
            {voidError && <Typography variant="body2" color="error" sx={{ mt: 2 }}>{voidError}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVoidOpen(false)} disabled={voiding}>Cancel</Button>
            {voidOpen === 'keep' ? (
              <Button variant="contained" onClick={() => decide('keep')} disabled={voiding}>
                {voiding ? 'Saving...' : 'Yes, keep the sale'}
              </Button>
            ) : (
              <Button variant="contained" color="error" onClick={() => decide('void')} disabled={voiding}>
                {voiding ? 'Voiding...' : 'Yes, void the sale'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Line items removed — Uber tablet handles item display.
            This page is for printing labels only. */}
      </CardContent>
    </Card>
  );
}
